import { ApiService, ApiServiceGetResponse } from '@services'
import {
  AchievementBackend,
  AchievementSlug,
  CollectedStarsStatisticBackend,
  MainInventoryBackendCard,
  UserCardsResponseBackend,
  UserProfileBackend,
  UserSkinsResponseBackend,
} from '@store'

type ConnectWalletResponse = string

type FetchUserWalletNonceResponse = string

type ConnectWalletPayload = {
  proof: {
    domain: {
      lengthBytes: number
      value: string
    }
    payload: string
    signature: string
    timestamp: number
  }
  wallet: {
    address: string
    publicKey: string
  }
}

export type ChannelSubscription = {
  channel_link: string,
  score: number,
  subscribed: boolean
}

export type SubscriptionStatus = {
  channel_link: string,
  redeemed: boolean,
  score_per_use: number
}
export type ClaimPromoCodeError =
  | 'bad_request'
  | 'not_found'
  | 'user_limit_reached'
  | 'too_many_requests'

export type ClaimPromoCodeResponse = {
  data: {
    code: string
    score_per_use: number
  }
  error_code: ClaimPromoCodeError
  ok: boolean
}

export const authUserApi = async (
  referrer?: number
): Promise<ApiServiceGetResponse<string>> => {
  const { ok, data } = await ApiService.post({
    endpoint: '/api/v1/user/auth',
    options: {
      headers: {
        'X-Telegram-InitData': window?.Telegram?.WebApp.initData,
      },
    },
    data:
      referrer !== undefined && referrer !== null ? { referrer } : undefined,
  })

  return { ok, data: data || null }
}

export const joinUserApi = async (
  refLink?: string
): Promise<{
  data: { [key: string]: any }
}> => {
  const response = await ApiService.post({
    endpoint: refLink ? `/api/v1/join?invite_hash=${refLink}` : '/api/v1/join',
    formData: window?.Telegram?.WebApp.initData,
  })

  return { data: response }
}

export const fetchProfileApi = async (): Promise<
  ApiServiceGetResponse<UserProfileBackend>
> => {
  const { ok, data } = await ApiService.get({
    endpoint: `/api/v1/user`,
  })

  return { ok, data }
}

export const completePrologueApi = async (): Promise<
  ApiServiceGetResponse<any>
> => {
  const { ok, data } = await ApiService.post({
    endpoint: '/api/v1/user/prologue',
  })

  return { ok, data: data || null }
}

export const updateUserSkinApi = async ({
  avatar,
  spiral,
  star,
  avatarEffect,
}: {
  avatar?: string
  spiral?: string
  star?: string
  avatarEffect?: string
}): Promise<ApiServiceGetResponse<any>> => {
  const { ok, data } = await ApiService.patch({
    endpoint: '/api/v1/user',
    data: {
      ...(avatar && { skin_avatar_item_id: avatar }),
      ...(spiral && { skin_spiral_item_id: spiral }),
      ...(star && { skin_stars_item_id: star }),
      ...(avatarEffect && { skin_avatar_effect_id: avatarEffect }),
    },
  })

  return { ok, data: data || null }
}

export const unlockAchievementApi = async (
  slug: AchievementSlug
): Promise<ApiServiceGetResponse<AchievementBackend>> => {
  const { ok, data } = await ApiService.post({
    endpoint: '/api/v1/user/achievement/unlock',
    data: { slug },
  })

  return { ok, data: data || null }
}

export const getUserAchievementsApi = async (): Promise<
  ApiServiceGetResponse<AchievementBackend[]>
> => {
  const { ok, data } = await ApiService.get({
    endpoint: '/api/v1/user/achievements',
  })

  return { ok, data: data || [] }
}

export const fetchUserSkinsApi = async (): Promise<
  ApiServiceGetResponse<UserSkinsResponseBackend>
> => {
  const { ok, data } = await ApiService.get({
    endpoint: '/api/v1/user/skins',
  })

  return { ok, data: data || null }
}

export const fetchUserCollectedStarsStatisticApi = async (): Promise<
  ApiServiceGetResponse<CollectedStarsStatisticBackend[]>
> => {
  const { ok, data } = await ApiService.get({
    endpoint: '/api/v1/user/stars-by-day',
  })

  return { ok, data: data || null }
}

export interface LeaderboardItemBackend {
  leaderboard: {
    avatar_effect: string
    avatar_icon: string
    is_premium: boolean
    rating: number
    username: string
  }[]
  me: {
    avatar_asset: {
      avatar_effect: string
      avatar_icon: string
    }
    is_premium: boolean
    position: number
    rating: number
    username: string
  }
  page: number
  total_score: number
}

export const getUserLeaderboardApi = async (): Promise<
  ApiServiceGetResponse<LeaderboardItemBackend>
> => {
  const { ok, data } = await ApiService.get({
    endpoint: '/api/v1/user/leaderboard',
    options: {
      page: 1,
      include_me: true,
    },
  })

  return { ok, data: data || null }
}

export const fetchUserCardsApi = async (): Promise<
  ApiServiceGetResponse<UserCardsResponseBackend[]>
> => {
  const { ok, data } = await ApiService.get({
    endpoint: '/api/v1/user/cards',
  })
  return { ok, data: data || null }
}
export const acceptPolicyApi = async (): Promise<
  ApiServiceGetResponse<UserProfileBackend>
> => {
  const { ok, data } = await ApiService.post({
    endpoint: '/api/v1/user/accept-policy',
  })

  return { ok, data: data || null }
}

export const connectWalletApi = async (
  payload: ConnectWalletPayload
): Promise<ApiServiceGetResponse<ConnectWalletResponse>> => {
  const { ok, data } = await ApiService.post({
    endpoint: '/api/v1/user/wallet/connect',
    data: payload,
  })

  return { ok, data: data || null }
}

export const fetchUserWalletNonce = async (): Promise<
  ApiServiceGetResponse<FetchUserWalletNonceResponse>
> => {
  const { ok, data } = await ApiService.get({
    endpoint: '/api/v1/user/wallet/nonce',
  })

  return { ok, data: data || null }
}

export const claimRewardByUUID = async (
  uuid: string
): Promise<ApiServiceGetResponse<MainInventoryBackendCard>> => {
  const { ok, data, error_code } = await ApiService.post({
    endpoint: `/api/v1/user/claim/${uuid}`,
  })

  return { ok, data: data || null, error: error_code }
}

export const claimPromoCode = async (
  code: string
): Promise<
  ApiServiceGetResponse<ClaimPromoCodeResponse['data']> & {
    error: ClaimPromoCodeError
  }
> => {
  const { ok, data, error_code } = await ApiService.get({
    endpoint: `/api/v1/user/redeem?code=${code}`,
  })

  return {
    ok,
    data: data || null,
    error: error_code as ClaimPromoCodeError,
  }
}

export const checkChannelSubscription = async (): Promise<ApiServiceGetResponse<ChannelSubscription>> => {
  const { ok, data, error_code } = await ApiService.post({
    endpoint: '/api/v1/user/check-channel-subscription',
  })

  return { ok, data: data || null, error: error_code }
}

export const getSubscriptionStatus = async (): Promise<ApiServiceGetResponse<SubscriptionStatus>> => {
  const { ok, data, error_code } = await ApiService.get({
    endpoint: '/api/v1/user/subscription-score-status',
  })

  return { ok, data: data || null, error: error_code }
}